import type { AdminPayoutDetailDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { MarkManualDialog } from './_components/MarkManualDialog';
import { PayoutDetailFooter } from './_components/PayoutDetailFooter';
import { PayoutDetailHeader } from './_components/PayoutDetailHeader';
import { PayoutOverviewTab } from './_components/PayoutOverviewTab';
import { RetryPayoutDialog } from './_components/RetryPayoutDialog';
import { useMarkManual } from './_lib/useMarkManual';
import { useRetryPayout } from './_lib/useRetryPayout';
import { useAdminPayoutDetail } from '../../api/admin-payouts';
import { EntityAuditTrail } from '../../components/audit';

/**
 * Payout detail drawer (AUT-128 / ADMIN-16).
 *
 * Right-side 720px drawer with tabs Обзор / Журнал + a footer with two
 * conditionally-rendered actions (Retry / Mark-manual), each role +
 * permission (`process_payouts`) + audit gated server-side (AUT-138 removed
 * per-action TOTP step-up). The buttons gate STRICTLY on the server-composed
 * `retryEligible` / `markManualEligible` flags (architect guardrail #3 —
 * never recomputed client-side).
 *
 * Those two flags live on the LIST item DTO, NOT the detail DTO (the
 * backend composes them only for the list projection). So the drawer
 * receives them as props sourced from the selected list row — it does
 * NOT re-derive the predicate from the detail. On success the
 * `['admin','payouts']` cache prefix is invalidated so the list + this
 * drawer refetch (NOT optimistic-patched — both action envelopes are the
 * LIST-shape `{dto, previousStatus}`, a different shape from the detail;
 * architect 🟠 #7).
 *
 * Журнал reuses the shared `<EntityAuditTrail entityType="Payout">`
 * placeholder — the SAME Phase-B stub the orders / reports drawers ship
 * (the backing `GET /admin/logs` endpoint lands in Phase D). No invented
 * endpoint; wiring the real fetch is a one-component swap.
 *
 * Opens when `payoutId` is non-null; the detail query is `enabled` only
 * then, so closing it stops the fetch.
 */
const DRAWER_WIDTH = 720;

type TabKey = 'overview' | 'log';

interface PayoutDetailDrawerProps {
  payoutId: string | null;
  /**
   * Eligibility flags sourced from the selected LIST row — the detail DTO
   * does not carry them (architect guardrail #3: gate on the
   * server-composed flags, never a client re-derivation). `false` while
   * no row is selected.
   */
  retryEligible: boolean;
  markManualEligible: boolean;
  onClose: () => void;
}

/** Tab nav + body — extracted to keep `DrawerBody` under the line cap. */
const DrawerTabs = ({
  detail,
  tab,
  onTabChange,
}: {
  detail: AdminPayoutDetailDto;
  tab: TabKey;
  onTabChange: (next: TabKey) => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <Tabs
        value={tab}
        onChange={(_e, v: TabKey) => onTabChange(v)}
        sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider', minHeight: 44 }}
      >
        <Tab value="overview" label={t('payouts.detail.tabs.overview')} />
        <Tab value="log" label={t('payouts.detail.tabs.log')} />
      </Tabs>
      <Box sx={{ flex: 1, overflow: 'auto', p: '22px 24px' }}>
        {tab === 'overview' ? <PayoutOverviewTab detail={detail} /> : null}
        {tab === 'log' ? <EntityAuditTrail entityType="Payout" entityId={detail.id} /> : null}
      </Box>
    </>
  );
};

const DrawerBody = observer(
  ({
    detail,
    retryEligible,
    markManualEligible,
    onClose,
    onBusyChange,
  }: {
    detail: AdminPayoutDetailDto;
    retryEligible: boolean;
    markManualEligible: boolean;
    onClose: () => void;
    /** Reports the in-flight flag up so the outer Drawer can gate `onClose`. */
    onBusyChange: (busy: boolean) => void;
  }): ReactElement => {
    const [tab, setTab] = useState<TabKey>('overview');
    const [retryOpen, setRetryOpen] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);

    const retry = useRetryPayout();
    const markManual = useMarkManual();
    const busy = retry.busy || markManual.busy;

    // Surface `busy` to the parent so a backdrop click can't dismiss the
    // Drawer mid-action (consistent with the inner dialogs' close-gating).
    useEffect(() => {
      onBusyChange(busy);
    }, [busy, onBusyChange]);

    const onConfirmRetry = (): void => {
      void retry.run(detail.id).then((ok) => {
        if (ok) {
          setRetryOpen(false);
          onClose();
        }
      });
    };

    const onConfirmManual = (note: string): void => {
      void markManual.run({ id: detail.id, note }).then((ok) => {
        if (ok) {
          setManualOpen(false);
          onClose();
        }
      });
    };

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            maxWidth: '100vw',
          }}
        >
          <PayoutDetailHeader detail={detail} onClose={onClose} />
          <DrawerTabs detail={detail} tab={tab} onTabChange={setTab} />
          <PayoutDetailFooter
            canRetry={retryEligible}
            canMarkManual={markManualEligible}
            busy={busy}
            onRetry={() => setRetryOpen(true)}
            onMarkManual={() => setManualOpen(true)}
          />
        </Box>

        <RetryPayoutDialog
          open={retryOpen}
          loading={busy}
          onConfirm={onConfirmRetry}
          onCancel={() => setRetryOpen(false)}
        />
        <MarkManualDialog
          open={manualOpen}
          loading={busy}
          onConfirm={onConfirmManual}
          onCancel={() => setManualOpen(false)}
        />
      </>
    );
  },
);

export const PayoutDetailDrawer = ({
  payoutId,
  retryEligible,
  markManualEligible,
  onClose,
}: PayoutDetailDrawerProps): ReactElement => {
  const { t } = useTranslation();
  const query = useAdminPayoutDetail(payoutId);
  const [busy, setBusy] = useState(false);

  // While an action is in flight, a backdrop / Esc dismissal is a no-op —
  // mirrors the inner dialogs' `onClose={loading ? undefined : onCancel}`
  // gating so the drawer can't vanish mid-mutation.
  const handleClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  // Clear any lingering busy flag once the drawer is fully closed so a
  // re-open can never start jammed (DrawerBody unmounts without a final
  // false report only in pathological cases).
  useEffect(() => {
    if (payoutId === null) setBusy(false);
  }, [payoutId]);

  return (
    <Drawer anchor="right" open={payoutId !== null} onClose={handleClose}>
      {query.isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      {query.error ? (
        <Box sx={{ width: { xs: '100vw', sm: DRAWER_WIDTH }, p: 3 }}>
          <Alert severity="error">{t('payouts.detail.loadError')}</Alert>
        </Box>
      ) : null}
      {query.data ? (
        <DrawerBody
          key={query.data.id}
          detail={query.data}
          retryEligible={retryEligible}
          markManualEligible={markManualEligible}
          onClose={onClose}
          onBusyChange={setBusy}
        />
      ) : null}
    </Drawer>
  );
};
